import type { DocMeta } from "./types";

const SLUG = "cookie-policy";
const id = (s: string) => `${SLUG}-${s}`;

export const COOKIE_POLICY_META: DocMeta = {
  slug: SLUG,
  title: "Cookie Policy",
  subtitle: "How Logical Health uses cookies and similar technologies",
  description:
    "The cookies and similar technologies we use, the consent banner, and how to manage your preferences.",
  lastUpdated: "May 6, 2026",
  sections: [
    { id: id("introduction"), title: "Introduction" },
    {
      id: id("what-are-cookies"),
      title: "1. What Are Cookies and Similar Technologies?",
    },
    { id: id("technologies-we-use"), title: "2. Technologies We Use" },
    {
      id: id("third-party-providers"),
      title: "3. Third-Party Providers",
    },
    {
      id: id("sensitive-information-and-phi"),
      title: "4. Sensitive Information and PHI",
    },
    { id: id("how-long-cookies-last"), title: "5. How Long Cookies Last" },
    {
      id: id("how-to-control-cookies"),
      title: "6. How to Control Cookies",
    },
    { id: id("do-not-track-signals"), title: "7. Do Not Track Signals" },
    {
      id: id("updates-to-this-cookie-policy"),
      title: "8. Updates to This Cookie Policy",
    },
    { id: id("more-information"), title: "9. More Information" },
    { id: id("contact-us"), title: "10. Contact Us" },
  ],
};

export function CookiePolicy() {
  return (
    <>
      <section id={id("introduction")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction</h2>
        <p>
          This Cookie Policy explains how Logical Health, Inc. (&ldquo;Logical
          Health&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, and
          &ldquo;our&rdquo;) uses cookies, analytics tags, local storage,
          pixels, and similar technologies on{" "}
          <strong>https://logical.health</strong> and related public website
          pages.
        </p>
        <p>
          This policy is intended to work together with our{" "}
          <a
            href="/legal#privacy-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Privacy Policy
          </a>
          . It describes the technologies we use, why we use them, and the
          controls available through browsers and service providers.
        </p>
        <p>
          A cookie banner and a <strong>Cookie Settings</strong> control are
          available on our website and allow visitors to accept or reject
          analytics technologies where required or supported. Your choice is
          remembered across visits, and the <strong>Cookie Settings</strong>{" "}
          link in the footer lets you reopen the banner and change your
          decision at any time. Browser, device, and provider opt-out
          controls described below remain available in addition to these
          in-site controls.
        </p>
      </section>

      <section id={id("what-are-cookies")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          1. What Are Cookies and Similar Technologies?
        </h2>
        <p>
          Cookies are small text files stored on your browser or device.
          Similar technologies, such as pixels, tags, scripts, SDKs, and
          local storage, can also store or read information from your
          browser or device.
        </p>
        <p>These technologies may help us:</p>
        <ul className="ml-6 space-y-2">
          <li>Operate and secure the website.</li>
          <li>Remember limited preferences or session information.</li>
          <li>Understand how visitors find and use the website.</li>
          <li>Measure performance and diagnose technical issues.</li>
          <li>Improve website content, forms, and user experience.</li>
        </ul>
      </section>

      <section id={id("technologies-we-use")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          2. Technologies We Use
        </h2>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          Strictly Necessary Technologies
        </h3>
        <p>
          These technologies support core website functionality, security,
          routing, form operation, and service availability. They may be
          set by our hosting platform, application framework, or other
          infrastructure used to deliver the site.
        </p>
        <p className="mt-4 text-sm text-gray-600">
          These technologies are generally required for the website to
          work and cannot always be disabled through our site.
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          Analytics and Performance Technologies
        </h3>
        <p>
          Where enabled, we use analytics tools to understand website
          traffic, page performance, user interactions, errors, and
          feature usage. This helps us improve website content, navigation,
          reliability, and product experience.
        </p>
        <p className="mt-4">
          Tools currently reflected in our website code include Google
          Analytics and PostHog. These tools may collect information such
          as pages visited, referring pages, browser and device details,
          approximate location inferred from IP address, clicks,
          interaction events, and technical error context.
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          Form and Scheduling Services
        </h3>
        <p>
          We use service providers to support contact forms, demo
          requests, and scheduling. Current website behavior includes
          EmailJS for contact-form delivery and Calendly links for meeting
          scheduling. These providers may set their own cookies or similar
          technologies when you interact with their services.
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          Advertising Technologies
        </h3>
        <p>
          We do not currently list third-party advertising or retargeting
          cookies as an active website control. If we begin using
          advertising cookies or similar tracking technologies, we will
          update this policy to describe those practices.
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-3">
          Specific Cookies and Storage Keys
        </h3>
        <p className="mb-4">
          The table below lists the cookies and browser-storage keys our
          website sets directly. Third-party providers such as Calendly and
          EmailJS may set their own cookies on their own domains when you
          interact with their services; we do not set those on
          logical.health.
        </p>
        <div
          className="overflow-x-auto mb-4"
          tabIndex={0}
          role="region"
          aria-label="Cookies and storage keys set by logical.health"
        >
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Set by</th>
                <th className="text-left px-4 py-3 font-semibold">Purpose</th>
                <th className="text-left px-4 py-3 font-semibold">Duration</th>
                <th className="text-left px-4 py-3 font-semibold">Category</th>
              </tr>
            </thead>
            <tbody className="bg-white text-gray-700">
              <tr className="border-t border-gray-200">
                <td className="px-4 py-3 font-mono text-xs">lh_consent</td>
                <td className="px-4 py-3">localStorage</td>
                <td className="px-4 py-3">Logical Health</td>
                <td className="px-4 py-3">
                  Stores your Accept or Reject decision so the banner does
                  not reappear and your analytics opt-out is honored on
                  future visits.
                </td>
                <td className="px-4 py-3">Persistent (until cleared)</td>
                <td className="px-4 py-3">Strictly Necessary</td>
              </tr>
              <tr className="border-t border-gray-200">
                <td className="px-4 py-3 font-mono text-xs">_ga</td>
                <td className="px-4 py-3">Cookie</td>
                <td className="px-4 py-3">Google Analytics</td>
                <td className="px-4 py-3">Distinguishes unique visitors.</td>
                <td className="px-4 py-3">2 years</td>
                <td className="px-4 py-3">Analytics</td>
              </tr>
              <tr className="border-t border-gray-200">
                <td className="px-4 py-3 font-mono text-xs">
                  _ga_&lt;MEASUREMENT_ID&gt;
                </td>
                <td className="px-4 py-3">Cookie</td>
                <td className="px-4 py-3">Google Analytics</td>
                <td className="px-4 py-3">
                  Persists session state for Google Analytics 4.
                </td>
                <td className="px-4 py-3">2 years</td>
                <td className="px-4 py-3">Analytics</td>
              </tr>
              <tr className="border-t border-gray-200">
                <td className="px-4 py-3 font-mono text-xs">
                  ph_&lt;key&gt;_posthog
                </td>
                <td className="px-4 py-3">localStorage</td>
                <td className="px-4 py-3">PostHog</td>
                <td className="px-4 py-3">
                  Anonymous device identifier and session metadata for
                  product analytics.
                </td>
                <td className="px-4 py-3">Persistent (until cleared)</td>
                <td className="px-4 py-3">Analytics</td>
              </tr>
              <tr className="border-t border-gray-200">
                <td className="px-4 py-3 font-mono text-xs">
                  ga-disable-&lt;MEASUREMENT_ID&gt;
                </td>
                <td className="px-4 py-3">Window flag</td>
                <td className="px-4 py-3">
                  Logical Health (set when you Reject)
                </td>
                <td className="px-4 py-3">
                  Disables Google Analytics from sending hits in the current
                  page load.
                </td>
                <td className="px-4 py-3">Page session</td>
                <td className="px-4 py-3">Strictly Necessary</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id={id("third-party-providers")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          3. Third-Party Providers
        </h2>
        <p>
          Some cookies and similar technologies are provided by third-party
          services. Their privacy and cookie practices are governed by their
          own policies.
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-8">
          Google Analytics
        </h3>
        <p className="mb-2">
          Used, where enabled, to understand website traffic, sessions,
          page views, and usage patterns.
        </p>
        <p className="text-sm text-gray-600">
          Learn more:{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            Google Privacy Policy
          </a>
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-8">
          PostHog
        </h3>
        <p className="mb-2">
          Used, where enabled, for product analytics, interaction events,
          and application error context.
        </p>
        <p className="text-sm text-gray-600">
          Learn more:{" "}
          <a
            href="https://posthog.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            PostHog Privacy Policy
          </a>
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-8">
          Calendly
        </h3>
        <p className="mb-2">
          Used for meeting scheduling links and scheduling workflows.
        </p>
        <p className="text-sm text-gray-600">
          Learn more:{" "}
          <a
            href="https://calendly.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            Calendly Privacy Policy
          </a>
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-8">
          EmailJS
        </h3>
        <p className="mb-2">
          Used for contact-form delivery and related form-submission
          processing.
        </p>
        <p className="text-sm text-gray-600">
          Learn more:{" "}
          <a
            href="https://www.emailjs.com/legal/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            EmailJS Privacy Policy
          </a>
        </p>
      </section>

      <section id={id("sensitive-information-and-phi")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          4. Sensitive Information and PHI
        </h2>
        <p>
          Logical Health primarily acts as a HIPAA Business Associate for
          Covered Entity clients in customer deployments. We do not
          intentionally use website analytics cookies to collect PHI/ePHI,
          and we apply minimization principles to analytics and telemetry
          collection.
        </p>
        <p className="mt-4">
          Do not submit PHI, medical details, or other sensitive health
          information through public website forms unless Logical Health or
          your health plan specifically instructs you to use that channel.
        </p>
      </section>

      <section id={id("how-long-cookies-last")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          5. How Long Cookies Last
        </h2>
        <p>
          Cookies and similar technologies may be session-based or
          persistent:
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-8">
          Session Technologies
        </h3>
        <p>
          These usually expire when you close your browser or end your
          browsing session.
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-8">
          Persistent Technologies
        </h3>
        <p>
          These may remain on your browser or device for a set period of
          time, until they expire, or until you delete them through your
          browser or device settings.
        </p>

        <p>
          The exact duration depends on the technology, browser, provider
          settings, and your device controls. Analytics cookies commonly
          persist for a limited period so providers can measure returning
          visits and usage patterns.
        </p>
      </section>

      <section id={id("how-to-control-cookies")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          6. How to Control Cookies
        </h2>
        <p>
          You can control cookies and similar technologies through our
          in-site controls described below, and through browser, device, and
          provider settings. If you block or delete cookies, some website
          features may not work as expected.
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          On Our Website
        </h3>
        <ul className="ml-6 space-y-3">
          <li>
            <strong>Cookie banner.</strong> A cookie banner is available on
            the website and offers <strong>Accept</strong> and{" "}
            <strong>Reject</strong> options. Accept allows analytics; Reject
            disables Google Analytics and opts you out of PostHog capture.
            Your decision is remembered across visits.
          </li>
          <li>
            <strong>Cookie Settings link in the footer.</strong> Available on
            every page. Click it to open the banner and change your decision
            at any time.
          </li>
          <li>
            <strong>When your decision takes effect.</strong> If you have
            previously rejected, analytics technologies are disabled at the
            start of every subsequent page load before they send any hits.
            If you have not yet decided on the current page load, analytics
            may run until you make a decision; once stored, your decision is
            honored on every subsequent page load.
          </li>
          <li>
            <strong>What &ldquo;Reject&rdquo; does technically.</strong> Sets{" "}
            <code className="font-mono text-xs">
              lh_consent=&quot;rejected&quot;
            </code>{" "}
            in your browser&apos;s local storage, sets the Google Analytics
            disable flag (
            <code className="font-mono text-xs">
              window[&quot;ga-disable-&lt;MEASUREMENT_ID&gt;&quot;] = true
            </code>
            ), and calls PostHog&apos;s opt-out so no further analytics hits
            are sent.
          </li>
        </ul>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          Browser Controls
        </h3>
        <p>
          Most browsers let you block, delete, or limit cookies through
          privacy and security settings. Common browser settings include:
        </p>
        <ul className="ml-6 space-y-2">
          <li>
            <strong>Google Chrome:</strong> Settings, Privacy and security,
            Third-party cookies.
          </li>
          <li>
            <strong>Mozilla Firefox:</strong> Settings, Privacy &amp;
            Security, Cookies and Site Data.
          </li>
          <li>
            <strong>Safari:</strong> Settings, Privacy, Manage Website Data.
          </li>
          <li>
            <strong>Microsoft Edge:</strong> Settings, Cookies and site
            permissions, Manage and delete cookies.
          </li>
        </ul>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          Analytics Controls
        </h3>
        <p>You can limit analytics collection by using:</p>
        <ul className="ml-6 space-y-2">
          <li>
            The{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              Google Analytics Opt-out Browser Add-on
            </a>
            .
          </li>
          <li>
            Browser privacy settings, tracker blocking, or content-blocking
            extensions.
          </li>
          <li>
            Privacy controls provided by your device, browser, or analytics
            providers.
          </li>
        </ul>

        <p className="mt-6">
          <strong>Important note:</strong> Browser and provider controls may
          not disable every technology in every context, and different
          browsers may handle controls differently.
        </p>
      </section>

      <section id={id("do-not-track-signals")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          7. Do Not Track Signals
        </h2>
        <p>
          Some browsers include a &ldquo;Do Not Track&rdquo; setting. Because
          there is no single industry standard for responding to these
          signals, our website does not currently respond to DNT browser
          signals in a uniform way. You can still control cookies through
          browser, device, and provider settings.
        </p>
      </section>

      <section id={id("updates-to-this-cookie-policy")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          8. Updates to This Cookie Policy
        </h2>
        <p>
          We may update this Cookie Policy from time to time to reflect
          changes in our website, vendors, technologies, legal requirements,
          or privacy practices. When we make changes, we will post the
          updated policy on this page and update the &ldquo;Last
          Updated&rdquo; date.
        </p>
      </section>

      <section id={id("more-information")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          9. More Information
        </h2>
        <p>
          For more information about how we collect, use, protect, retain,
          and share information, please see our{" "}
          <a
            href="/legal#privacy-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Privacy Policy
          </a>
          .
        </p>
      </section>

      <section id={id("contact-us")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          10. Contact Us
        </h2>
        <p>
          If you have questions or concerns about this Cookie Policy or our
          use of cookies and similar technologies, contact us at{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>
          .
        </p>
      </section>
    </>
  );
}
